
import { Component, Vue, Watch } from 'vue-property-decorator';
import moment from 'moment';
import { RepositoryFactory } from '@/api/RepositoryFactory';
import SmsRepository from '@/api/repositories/SmsRepository';

type AccountType = 'CUSTOMER' | 'DRIVER';

interface IssuedCodeRow {
    id: string,
    status: 'LIVE' | 'USED' | 'DEAD' | 'EXPIRED',
    attempts: number,
    issuedByLabel: string | null,
    reason: string,
    createdAt: string,
    expiresAt: string,
    usedAt: string | null,
    deadAt: string | null
}

interface LoginCodeStatus {
    phone: string,
    issuedCodes: IssuedCodeRow[],
    window: { endsAt: string, wrongCodes: number, wrongCodeLimit: number, codeSends: number, codeSendLimit: number }
}

const smsRepository: SmsRepository = RepositoryFactory.get('sms');

/**
 * Support login codes: the replacement for the removed global super OTP.
 *
 * An admin issues a one-time code for ONE account after the identity check in
 * the backend's SUPPORT_LOGIN_CODE.md (call the registered number back, read
 * the code only there). The backend returns the code exactly once; this card
 * shows it until the admin hides it and never stores it anywhere. Status shows
 * each recent code as live / used / dead / expired with who issued it and why --
 * the user is never told which, so this is where support finds out.
 */
@Component({})
export default class SupportLoginCodeComponent extends Vue {
    /** Mirrors the backend limits (verification service); used for display only. */
    public readonly MAX_WRONG = 3;
    public readonly REASON_MIN = 5;
    public readonly REASON_MAX = 500;

    public type: AccountType = 'CUSTOMER';
    public prefix: string = '+264';
    public phoneNumber: string = '';
    public reason: string = '';

    public isIssuing: boolean = false;
    public isLoadingStatus: boolean = false;

    /** The code just issued, shown once. Cleared by "hide", by changing the phone, and on leaving. */
    public issued: { code: string, expiresAt: string, phone: string } | null = null;
    public status: LoginCodeStatus | null = null;

    public get phone(): { prefix: string, phoneNumber: string } {
        return {
            prefix: this.prefix.trim(),
            phoneNumber: this.phoneNumber.replace(/\s+/g, '')
        };
    }

    public get phoneValid(): boolean {
        return /^\+\d{1,4}$/.test(this.phone.prefix) && /^\d{5,15}$/.test(this.phone.phoneNumber);
    }

    public get canIssue(): boolean {
        const length = this.reason.trim().length;
        return this.phoneValid && length >= this.REASON_MIN && length <= this.REASON_MAX && !this.isIssuing;
    }

    public get budgetSpent(): boolean {
        return !!this.status && this.status.window.wrongCodes >= this.status.window.wrongCodeLimit;
    }

    /** A different account: the code and status on screen belong to the previous one. */
    @Watch('type')
    @Watch('prefix')
    @Watch('phoneNumber')
    private onAccountChange() {
        this.issued = null;
        this.status = null;
    }

    public beforeDestroy() {
        this.issued = null;
    }

    public async issue() {
        if (!this.canIssue) {
            return;
        }
        const phone = this.phone;
        try {
            this.isIssuing = true;
            const response = await smsRepository.issueLoginCode(this.type, phone, this.reason.trim());
            this.issued = {
                code: response.data.code,
                expiresAt: response.data.expiresAt,
                phone: `${phone.prefix} ${phone.phoneNumber}`
            };
            this.reason = '';
            await this.loadStatus();
        } catch (e) {
            // ApiError from EntityBaseRepository: { status, message }.
            const status = (e as { status?: number } | null)?.status;
            if (status === 404) {
                Vue.notifyErrorSimplified('SMS_MONITOR.LOGIN_CODE.NOT_FOUND');
            } else if (status === 422) {
                Vue.notifyErrorSimplified('SMS_MONITOR.LOGIN_CODE.NOT_ISSUABLE');
            } else {
                Vue.notifyErrorSimplified('SMS_MONITOR.LOGIN_CODE.ISSUE_ERROR');
            }
        } finally {
            this.isIssuing = false;
        }
    }

    public async loadStatus() {
        if (!this.phoneValid) {
            return;
        }
        const phone = this.phone;
        try {
            this.isLoadingStatus = true;
            const response = await smsRepository.getLoginCodeStatus(this.type, phone);
            this.status = {
                phone: `${phone.prefix} ${phone.phoneNumber}`,
                issuedCodes: response.data.issuedCodes ?? [],
                window: response.data.window
            };
        } catch (e) {
            Vue.notifyErrorSimplified('SMS_MONITOR.LOGIN_CODE.STATUS_ERROR');
        } finally {
            this.isLoadingStatus = false;
        }
    }

    public hideCode() {
        this.issued = null;
    }

    public formatTime(value: string | null): string {
        return value ? moment(value).format('YYYY-MM-DD HH:mm') : '';
    }

    public stateColor(state: string): string {
        switch (state) {
            case 'LIVE': return 'green';
            case 'USED': return 'blue';
            case 'DEAD': return 'red';
            default: return 'grey';
        }
    }
}
